/* 郝昭（字伯道） */
import { SG, K, gd, spMul, landing } from '../../core/kit.ts';
import type { Unit } from '../types.ts';
import { defineHero, hook, D, P } from '../define.ts';

const CHENCANG = 100, CHENCANG_MAX = 30; // 天賦「陳倉」：防禦擋下多少傷害換 1 點攻擊；最多 +30（v0.5.3.7）

export default defineHero({
  id: 'haozhao', name: '郝昭', sub: '字伯道', faction: 'wei', role: '射手', weapon: 'bow',
  look: { wart: 'xbow', weaponName: '守城弩弓', head: 'cap', beard: 'long', bulk: 1.06, fx: '#ff8a3a', c: { armor: '#4a5670', sleeve: '#3a4458', cape: '#5a4030' } },
  // v0.5.2：天賦改成專屬「陳倉」（原本是通用的被圍減傷）；火箭貫穿、城壘射程 +200 並加攻速、重門改成防禦多擋傷害
  passive: ['陳倉', `按住防禦（D）每擋下 ${CHENCANG} 傷害，攻擊 +1，最多 +${CHENCANG_MAX}（整場累積，陣亡不歸零）。`, { stack: '攻擊', max: CHENCANG_MAX }],
  skills: [
    ['火箭', 'bolt', '朝方向射出貫穿的火箭，路徑上每個敵人受到 {80/125/170/215} 火傷害並點燃；對召喚物、攻城器械三倍傷害。', { x: { k: 'firearrow', dmg: [80, 125, 170, 215] }, aim: D(800, 20), range: 800, cd: 8, mp: [60, 70, 80, 90], el: 'fire' }],
    // v0.4.2：城壘改成面向敵方的一排城垛，減傷固定 15% 但城垛後移速剩 20%；v0.5.2：改成置放技、射程 +200、加攻速
    ['陳倉城壘', 'shield', '在指定地點築起一排面向敵方的城垛 {4/4.5/5/5.5} 秒（長 140）。站在城垛後：普攻射程 +200、攻速 +{20/30/40/50}%、受到的遠程傷害 -15%，但移速只剩 20%。', { x: { k: 'rampart', dur: [4, 4.5, 5, 5.5], dr: 15, r: 120, half: 70, range: 200, as: [20, 30, 40, 50] }, aim: P(415, 120, [70, 30], { face: 'home', off: 120 }), cd: [24, 22, 20, 18], mp: [80, 90, 100, 110], el: 'earth' }],
    // v0.5.2：改成防禦（D）多擋傷害（原本約擋一半，這裡再加上整擊的 p%）
    ['重門', 'passive', '按住防禦（D）時，多擋下這一擊 {12/18/24/30}% 的傷害（防禦原本約擋一半）。', { pv: {}, sig: 'gate', p: [12, 18, 24, 30] }],
    ['千人拒十萬', 'summon', '在指定地點架起 3 座箭樓 {8/10/12} 秒（不能移動、生命 {300/450/600}），每秒朝 650 內的敵人射箭 {30/45/60}；郝昭在箭樓 500 內時箭樓攻速 +50%。', { x: { k: 'towers', dur: [8, 10, 12], hp: [300, 450, 600], dmg: [30, 45, 60] }, aim: P(600, 160), cd: [100, 85, 70], mp: [150, 175, 200], el: null, shout: '昭在，陳倉不可得也！' }],
  ],
  // 專屬技能（技能資料裡的 x.k）
  kinds: {
    firearrow(u, a, X) {
      const sp = spMul(u);
      this.shootProj(u, { dir: a.dir, speed: 1000, range: this.skillRange(a.sk, a.lvl), shape: 'arrow', col: '#ff8a3a', el: 'fire',
        filter: (t: Unit) => !t.structure,
        onHit: (t: Unit) => { // 貫穿：打到誰都不停
          this.damage(u, t, X.dmg * sp * (t.summon || t.siege ? 3 : 1), { magic: true, spell: true, stagger: 0.2, fire: true });
          if (t.alive) this.ignite(t, X.dmg * 0.08, 3, u);
          return false;
        } });
    },
    rampart(u, a, X) {
      // 一排城垛：築在指定地點，擋在背對自家本營的那一側；電腦直接築在自己腳下（它瞄的是敵人）
      const c = u.human ? landing(u.x, u.y, a.pt[0], a.pt[1]) : [u.x, u.y], o = { x: c[0], y: c[1] };
      const home = SG.MAP.bases[u.team], back = SG.dirTo(o, home.x, home.y), side = [-back[1], back[0]], r = X.r;
      const at = (dx: number, dy: number) => [o.x + dx, o.y + dy * K];
      const fx = -back[0] * r, fy = -back[1] * r, sx = side[0] * X.half, sy = side[1] * X.half; // 城垛長 2×half，站位範圍照 r
      const f1 = at(fx + sx, fy + sy), f2 = at(fx - sx, fy - sy);
      this.addWall([f1[0], f1[1], f2[0], f2[1]], 30, X.dur, u.team, 'city');
      u.rampart = { x: o.x, y: o.y, r: r + 10, until: this.t + X.dur, dr: X.dr, range: X.range, as: X.as };
      this.fx({ k: 'slam', x: o.x, y: o.y, col: '#d8a860' });
    },
    towers(u, a, X) {
      this.units.forEach((o) => { if (o.alive && o.arrowTower && o.owner === u) { o.alive = false; o.deadT = this.t; this.fx({ k: 'puff', x: o.x, y: o.y }); } });
      const c = a.pt, sp = spMul(u);
      for (let j = 0; j < 3; j++) {
        const ang = (j / 3) * Math.PI * 2 + 0.5, p = landing(u.x, u.y, c[0] + Math.cos(ang) * 110, c[1] + Math.sin(ang) * 110 * K);
        const s = this.spawnMinion(u.team, null, p[0], p[1], true);
        Object.assign(s, { temp: X.dur, owner: u, summon: true, still: true, ms: 0, ranged: true, range: 650, maxHp: X.hp, hp: X.hp, atk: X.dmg * sp, asp: 125, armor: 8, r: 26, name: '箭樓', arrowTower: true, beast: 'tower' }); // beast：只影響畫面
        this.fx({ k: 'slam', x: p[0], y: p[1], col: '#d8a860' });
      }
    },
  },
  // 被動與專屬效果：hook(時間點, 順位, 處理)（時間點一覽見 core/hooks.ts）
  hooks: [
    // 陳倉城壘：站在城垛內射程增加
    hook('step.hero', 30, function (u, dt) {
      const rp = u.rampart;
      if (!rp) return;
      const inside = rp.until > this.t && gd(u.x, u.y, rp.x, rp.y) <= rp.r;
      this.setRangeBonus(u, 'rampart', inside ? rp.range : 0);
      if (inside) this.addBuff(u, { id: 'rampart', t: 0.1, ms: -0.8, as: rp.as }); // 城垛後移速剩 20%、攻速提升
      if (rp.until <= this.t) u.rampart = null;
    }),
    // 陳倉城壘：城垛內受到的遠程傷害減少
    hook('damage.before', 25, function (c) {
      const t = c.t, rp = t.rampart;
      if (rp && rp.until > this.t && gd(t.x, t.y, rp.x, rp.y) <= rp.r && (c.own.ranged || (c.o && c.o.proj))) c.a *= 1 - rp.dr / 100;
    }),
    // 重門：防禦多擋的比例（sim 的防禦處理讀 u.blockAdd）
    hook('recalc', 30, function (u, sg) { if (u.spec && u.spec.id === 'haozhao') u.blockAdd = sg && sg.gate ? sg.gate.v('p') / 100 : 0; }),
    // 陳倉（天賦）：防禦累計擋下的傷害每 CHENCANG 點，攻擊 +1
    hook('step.hero', 31, function (u) {
      if (u.spec.id !== 'haozhao' || !u.stat) return;
      const n = Math.min(CHENCANG_MAX, Math.floor(u.stat.blocked / CHENCANG));
      if (n !== (u.ccN || 0)) { u.ccN = u.talN = n; this.recalc(u); } // talN：天賦按鈕上顯示的層數
    }),
    hook('recalc', 31, function (u) { if (u.spec && u.spec.id === 'haozhao' && u.ccN) u.atk += u.ccN; }),
    // 防禦（電腦）：被敵將打的時候偶爾舉盾 1.2 秒累積陳倉；殘血就不舉，回去打
    hook('control.pre', 30, function (u, dt) {
      if (u.human || !u.ai || u.spec.id !== 'haozhao') return;
      const I = u.input;
      if (u.aiGuard > this.t) { if (u.hp / u.maxHp > 0.35) { I.defend = true; I.attack = false; } else u.aiGuard = 0; return; }
      const hit = u.dmgBy && Object.values(u.dmgBy).some((v: any) => v > this.t - 0.5);
      if (hit && u.hp / u.maxHp > 0.5 && SG.rand() < dt * 1.5) u.aiGuard = this.t + 1.2;
    }),
    // 千人拒十萬：郝昭在箭樓附近時，箭樓攻速 +50%
    hook('aura', 40, function () {
      for (const s of this.units) if (s.alive && s.arrowTower && s.owner && s.owner.alive && gd(s.x, s.y, s.owner.x, s.owner.y) <= 500) this.addBuff(s, { id: 'tower', t: 0.7, as: 50 });
    }),
    hook('revive', 70, function (u) { if (u.rampart) { u.rampart = null; this.setRangeBonus(u, 'rampart', 0); } u.aiGuard = 0; }),
  ],
  // 電腦什麼時候放這招
  ai: {
    firearrow: ({ t, d }) => !!t && d < 760,
    rampart: ({ u, hero, d, foeHeroes }) => hero && d < 700 && foeHeroes(800) >= 1,
    towers: ({ hero, d, foeHeroes, t }) => (hero && d < 650 && foeHeroes(900) >= 2) || (!!t && !!t.structure && d < 600),
  },
});
